import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import React from 'react';

import HeaderSection from '../components/layout/sections/HeaderSection';
import PageSection from '../components/layout/sections/PageSection';
import { withGlobalDefaultStaticProps } from '../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../lib/seo/pageSeoConfigs';
import pageRoutes from '../router/pageRoutes';
import { NextPageWithLayout } from '../types/global-types';

const TabPanels = dynamic(() => import('../components/interactive/TabPanels'), { ssr: false });

type PrivacyPolicyPageStaticProps = {};

export const getStaticProps: GetStaticProps<PrivacyPolicyPageStaticProps> = async (context) => {
  const props: PrivacyPolicyPageStaticProps = {};
  return withGlobalDefaultStaticProps(context, { props });
};

const PrivacyPolicyPage: NextPageWithLayout<PrivacyPolicyPageStaticProps> = (_props) => {
  const seoConfig = getPageSeoConfig(pageRoutes.PrivacyPolicy);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        title="Privacy Policy"
        description={
          <div>
            <p>
              PRIVACY POLICY GASPARDESK, INC. (the “Company”) is committed to maintaining robust privacy protections for its users. Our
              Privacy Policy (“Privacy Policy”) is designed to help you understand how we collect, use and safeguard the information you
              provide to us and to assist you in making informed decisions when using our Service.
            </p>
            <p>
              For purposes of this Agreement, “Site” refers to the Company’s website, which can be accessed at
              <Link target="_blank" rel="nofollow" href={pageRoutes.Index}>
                https://www.gaspar.ai/
              </Link>
              .
            </p>
            <p>
              “Service” refers to the Company’s services accessed via the Site, in which users may contact the Company, request a
              demonstration of Company product(s), submit various tickets, manage various tickets, manage user-defined enterprise services,
              among many other actions.
            </p>
            <p>The terms “we,” “us,” and “our” refer to the Company.</p>
            <p>“You” refers to you, as a user of our Site or our Service.</p>
            <p>
              By accessing our Site or our Service, you accept our Privacy Policy and
              <Link target="_blank" rel="nofollow" href={pageRoutes.TermsAndConditions}>
                Terms of Use
              </Link>
              , and you consent to our collection, storage, use and disclosure of your Personal Information as described in this Privacy
              Policy.
            </p>
          </div>
        }
      />
      <PageSection>
        <h1>In Depth</h1>
        <TabPanels
          panelColumnMinWidth={400}
          panels={[
            {
              tabId: 'information-we-collect',
              tabLabel: 'I. Information We Collect',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>I. Information We Collect</h3>
                  <p>
                    We collect “Non-Personal Information” and “Personal Information.” Non-Personal Information includes information that
                    cannot be used to personally identify you, such as anonymous usage data, general demographic information we may collect,
                    referring/exit pages and URLs, platform types, preferences you submit and preferences that are generated based on the
                    data you submit and the number of clicks. Personal Information includes your email, your name, and whatever information
                    you might enter into the message form, which you submit to us through at the Site.
                  </p>
                  <h3>1. Information collected via Technology</h3>
                  <p>
                    To activate the Service you do not need to submit any Personal Information other than your email address. To use the
                    Service thereafter, you may need to submit further Personal Information. However, in an effort to improve the quality of
                    the Service, we track information provided to us by your browser or by our software application when you view or use the
                    Service, such as the website you came from (known as the “referring URL”), the type of browser you use, the device from
                    which you connected to the Service, the time and date of access, and other information that does not personally identify
                    you. We track this information using cookies, or small text files which include an anonymous unique identifier. Cookies
                    are sent to a user’s browser from our servers and are stored on the user’s computer hard drive. Sending a cookie to a
                    user’s browser enables us to collect NonPersonal information about that user and keep a record of the user’s preferences
                    when utilizing our services, both on an individual and aggregate basis. The Company may use both persistent and session
                    cookies; persistent cookies remain on your computer after you close your session and until you delete them, while
                    session cookies expire when you close your browser.
                  </p>
                  <h3>2. Information you provide us by registering for an account</h3>
                  <p>
                    In addition to the information provided automatically by your browser when you visit the Site, to become a subscriber to
                    the Service you will need to create a personal profile. You can create a profile and use the Service by entering your
                    work email address. Many users can use SSO to log on with their work account, e.g. Microsoft 365 or G-Suite. Some may
                    need to create a user name and a password. By registering, you are authorizing us to collect, store, and use your email
                    address in accordance with this Privacy Policy.
                  </p>
                  <h3>3. Children’s Privacy</h3>
                  <p>
                    The Site and the Service are not directed to anyone under the age of 16. The Site does not knowingly collect or solicit
                    information from anyone under the age of 16, or allow anyone under the age of 16 to sign up for the Service. In the
                    event that we learn that we have gathered personal information from anyone under the age of 16 without the consent of a
                    parent or guardian, we will delete that information as soon as possible. If you believe we have collected such
                    information, please contact us immediately at
                    <Link target="_blank" rel="nofollow" href="mailto:support@gaspar.ai">
                      support@gaspar.ai
                    </Link>
                    .
                  </p>
                </div>
              ),
            },
            {
              tabId: 'how-we-use-and-share-information',
              tabLabel: 'II. How We Use and Share Information',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>II. How We Use and Share Information</h3>
                  <h4>Personal Information:</h4>
                  <p>
                    Except as otherwise stated in this Privacy Policy, we do not sell, trade, rent, or otherwise share for marketing
                    purposes your Personal Information with third parties without your consent. We do share Personal Information with
                    vendors who are performing services for the Company, such as the servers for our email communications who are provided
                    access to the user’s email address for purposes of sending emails from us. Those vendors use your Personal Information
                    only at our direction and in accordance with our Privacy Policy. In general, the Personal Information you provide to us
                    is used to help us communicate with you. For example, we use Personal Information to contact users in response to
                    questions, solicit feedback from users, provide technical support, and inform users about promotional offers. We may
                    share Personal Information with outside parties if we have a good-faith belief that access, use, preservation or
                    disclosure of the information is reasonably necessary to meet any applicable legal process or enforceable governmental
                    request; to enforce applicable Terms of Service, including investigation of potential violations; address fraud,
                    security or technical concerns; or to protect against harm to the rights, property, or safety of our users or the public
                    as required or permitted by law.
                  </p>
                  <h4>Non-Personal Information:</h4>
                  <p>
                    In general, we use Non-Personal Information to help us improve the Service and customize the user experience. We also
                    aggregate Non-Personal Information in order to track trends and analyze use patterns on the Site. This Privacy Policy
                    does not limit in any way our use or disclosure of Non-Personal Information and we reserve the right to use and disclose
                    such Non-Personal Information to our partners, advertisers and other third parties at our discretion.
                  </p>
                  <p>
                    In the event we undergo a business transaction such as a merger, acquisition by another company, or sale of all or a
                    portion of our assets, your Personal Information may be among the assets transferred. You acknowledge and consent that
                    such transfers may occur and are permitted by this Privacy Policy, and that any acquirer of our assets may continue to
                    process your Personal Information as set forth in this Privacy Policy. If our information practices change at any time
                    in the future, we will post the policy changes to the Site so that you may opt out of the new information practices. We
                    suggest that you check the Site periodically if you are concerned about how your information is used.
                  </p>
                  <p>
                    We prioritize the privacy and security of your data. Thus, the information collected from our customers is strictly
                    confidential and will never be shared with other customers or utilized to train our generic AI model. The sole purpose
                    of collecting these data is to enhance and personalize your individual model for an improved and tailored experience.
                  </p>
                </div>
              ),
            },
            {
              tabId: 'how-we-protect-information',
              tabLabel: 'III. How We Protect Information',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>III. How We Protect Information</h3>
                  <p>
                    We implement security measures designed to protect your information from unauthorized access. Your account is protected
                    by your account password and we urge you to take steps to keep your personal information safe by not disclosing your
                    password and by logging out of your account after each use. We further protect your information from potential security
                    breaches by implementing certain technological security measures including encryption, firewalls and secure socket layer
                    technology. However, these measures do not guarantee that your information will not be accessed, disclosed, altered or
                    destroyed by breach of such firewalls and secure server software. By using our Service, you acknowledge that you
                    understand and agree to assume these risks.
                  </p>
                </div>
              ),
            },
            {
              tabId: 'your-rights-regarding-the-use-of-your-personal-information',
              tabLabel: 'IV. Your Rights Regarding the Use of Your Personal Information',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>IV. Your Rights Regarding the Use of Your Personal Information</h3>
                  <p>
                    You have the right at any time to prevent us from contacting you for marketing purposes. When we send a promotional
                    communication to a user, the user can opt out of further promotional communications by following the unsubscribe
                    instructions provided in each promotional e-mail.
                  </p>
                  <p>
                    If you wish to permanently delete your data (i) if you are a User, contact your Administrator and (ii) if you are an
                    Administrator, contact us at
                    <Link target="_blank" rel="nofollow" href="mailto:support@gaspar.ai">
                      support@gaspar.ai
                    </Link>
                    with your request. With Administrator approval, we will either, at your discretion (a) permanently delete your
                    information or (b) archive it in case you wish to come back in the future. &ZeroWidthSpace; We are deleting data and
                    personal information when the company that you work for does not use any longer our service.
                  </p>
                  <p>
                    Please note that notwithstanding the promotional preferences you indicate by either unsubscribing or opting out, we may
                    continue to send you administrative emails including, for example, periodic updates to our Privacy Policy.
                  </p>
                </div>
              ),
            },
            {
              tabId: 'links-to-other-websites',
              tabLabel: 'V. Links to Other Websites',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>V. Links to Other Websites</h3>
                  <p>
                    As part of the Service, we may provide links to or compatibility with other websites or applications. However, we are
                    not responsible for the privacy practices employed by those websites or the information or content they contain. This
                    Privacy Policy applies solely to information collected by us through the Site and the Service. Therefore, this Privacy
                    Policy does not apply to your use of a third-party website accessed by selecting a link on our Site or via our Service.
                    To the extent that you access or use the Service through or on another website or application, then the privacy policy
                    of that other website or application will apply to your access or use of that site or application. We encourage our
                    users to read the privacy statements of other websites before proceeding to use them.
                  </p>
                </div>
              ),
            },
            {
              tabId: 'changes-to-our-privacy-policy',
              tabLabel: 'VI. Changes to Our Privacy Policy',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>VI. Changes to Our Privacy Policy</h3>
                  <p>
                    The Company reserves the right to change this policy and our Terms of Service at any time. We will notify you of
                    significant changes to our Privacy Policy by sending a notice to the primary email address specified in your account or
                    by placing a prominent notice on our site. Significant changes will go into effect 30 days following such notification.
                    Non-material changes or clarifications will take effect immediately, with or without notice unless required by law. You
                    should periodically check the Site and this privacy page for updates. VII. CONTACT US If you have any questions
                    regarding this Privacy Policy or the practices of this Site, please contact us by sending an email to
                    <Link target="_blank" rel="nofollow" href="mailto:support@gaspar.ai">
                      support@gaspar.ai
                    </Link>
                    .
                  </p>
                </div>
              ),
            },
            {
              tabId: 'compliance-with-limited-use-policy',
              tabLabel: 'VII. Compliance with Limited Use Policy',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>VII. Compliance with Limited Use Policy</h3>
                  <p>
                    Gaspardesk&#39;s use and transfer of information received from Google APIs to any other app will adhere to
                    <Link
                      target="_blank"
                      rel="nofollow"
                      href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes"
                    >
                      Google API Services User Data Policy
                    </Link>
                    , including the Limited Use requirements.
                  </p>
                </div>
              ),
            },
            {
              tabId: 'data-retention-policy',
              tabLabel: 'VIII. Data Retention Policy',
              content: (
                <div className="flex flex-col gap-4">
                  <h3>VIII. Data Retention Policy</h3>
                  <p>
                    Customer data is retained for as long as the account is in active status. Data enters an “expired” state when the
                    account is voluntarily closed. Expired account data will be retained for 14 days. After this period, the account and
                    related data will be removed. Customers that wish to voluntarily close their account should download their data manually
                    or via requesting an export at the support email address, prior to closing their account. If a customer account is
                    involuntarily suspended, then there is a 90 day grace period during which the account will be inaccessible but can be
                    reopened if the customer meets their payment obligations and resolves any terms of service violations. If a customer
                    wishes to manually backup their data in a suspended account, then they must ensure that their account is brought back to
                    good standing so that the user interface will be available for their use. After 90 days, the suspended account will be
                    closed and permanently removed thereafter (except when required by law to retain).
                  </p>
                </div>
              ),
            },
          ]}
        />
      </PageSection>
    </>
  );
};
PrivacyPolicyPage.displayName = 'PrivacyPolicyPage';
export default PrivacyPolicyPage;
